import { useCallback, useEffect, useState } from "react";
import { Loader2, Mail, RefreshCw, Send } from "lucide-react";
import api from "../api/axios";

export default function AdminNewsletterPage() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [campaignStatus, setCampaignStatus] = useState(null);

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/newsletter/subscribers");
      setSubscribers(Array.isArray(data) ? data : []);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Failed to load newsletter subscribers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscribers();
  }, [fetchSubscribers]);

  const sendNewsletter = async (event) => {
    event.preventDefault();
    if (sending) return;

    const form = event.currentTarget;
    const formData = new FormData(form);
    setSending(true);
    setError("");
    setCampaignStatus(null);

    try {
      const { data } = await api.post("/admin/newsletter/send", {
        subject: formData.get("subject"),
        body: formData.get("body"),
      });
      setCampaignStatus(data);
      if (data.failedCount === 0) form.reset();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Newsletter delivery failed.");
    } finally {
      setSending(false);
    }
  };

  const subscribedCount = subscribers.filter((subscriber) => subscriber.status === "subscribed").length;
  const unsubscribedCount = subscribers.length - subscribedCount;
  const visibleSubscribers = subscribers.filter((subscriber) => filter === "all" || subscriber.status === filter);

  return (
    <div className="mx-auto max-w-7xl space-y-6 text-white">
      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm uppercase tracking-[0.24em] text-cyan-300/80">Admin</p>
            <h1 className="mt-2 text-3xl font-bold">Newsletter</h1>
            <p className="mt-2 text-sm text-slate-400">Review subscribers and send updates to active subscribers.</p>
          </div>
          <button
            type="button"
            onClick={fetchSubscribers}
            disabled={loading}
            aria-label="Refresh subscribers"
            title="Refresh subscribers"
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-cyan-300/30 bg-cyan-300/10 text-cyan-300 transition hover:bg-cyan-300/20 disabled:opacity-60"
          >
            <RefreshCw size={17} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-white/10 bg-slate-900/75 p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">All subscribers</p>
          <p className="mt-3 text-3xl font-bold">{subscribers.length}</p>
        </div>
        <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/5 p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-200/80">Subscribed</p>
          <p className="mt-3 text-3xl font-bold text-emerald-300">{subscribedCount}</p>
        </div>
        <div className="rounded-2xl border border-slate-400/20 bg-slate-400/5 p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-300/80">Unsubscribed</p>
          <p className="mt-3 text-3xl font-bold text-slate-300">{unsubscribedCount}</p>
        </div>
      </section>

      {error && <p role="alert" className="rounded-xl border border-red-400/20 bg-red-400/10 p-3 text-sm text-red-200">{error}</p>}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(340px,0.85fr)]">
        <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-4 backdrop-blur-xl md:p-6">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Mail className="text-cyan-300" size={20} />
              <h2 className="font-semibold">Subscribers</h2>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-400">
              Status
              <select
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                className="rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-cyan-300/50"
              >
                <option value="all">All</option>
                <option value="subscribed">Subscribed</option>
                <option value="unsubscribed">Unsubscribed</option>
              </select>
            </label>
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-400">
              <Loader2 className="animate-spin text-cyan-300" size={18} /> Loading subscribers...
            </div>
          ) : visibleSubscribers.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">
              {subscribers.length ? "No subscribers match this filter." : "No newsletter subscribers yet."}
            </p>
          ) : (
            <div className="max-h-[34rem] space-y-2 overflow-y-auto pr-1">
              {visibleSubscribers.map((subscriber) => (
                <div key={subscriber._id} className="flex flex-col gap-2 rounded-xl border border-white/10 bg-white/[0.04] p-3 sm:flex-row sm:items-center sm:justify-between">
                  <a href={`mailto:${subscriber.email}`} className="break-all text-sm text-cyan-200 hover:underline">{subscriber.email}</a>
                  <div className="flex items-center gap-3 text-xs">
                    <span className={subscriber.status === "subscribed" ? "text-emerald-300" : "text-slate-400"}>
                      {subscriber.status === "subscribed" ? "Subscribed" : "Unsubscribed"}
                    </span>
                    <span className="text-slate-500">{new Date(subscriber.subscribedAt).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl md:p-6">
          <div className="mb-5 flex items-center gap-2">
            <Send className="text-cyan-300" size={19} />
            <h2 className="font-semibold">Compose newsletter</h2>
          </div>
          <p className="mb-5 text-sm text-slate-400">This will send an individual email to each of the {subscribedCount} active subscribers.</p>
          <form onSubmit={sendNewsletter} className="space-y-4">
            <div>
              <label htmlFor="newsletter-subject" className="mb-2 block text-sm font-medium text-slate-200">Subject</label>
              <input
                id="newsletter-subject"
                name="subject"
                type="text"
                maxLength={160}
                required
                className="w-full rounded-lg border border-white/10 bg-slate-950 px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-300/50"
              />
            </div>
            <div>
              <label htmlFor="newsletter-body" className="mb-2 block text-sm font-medium text-slate-200">Message</label>
              <textarea
                id="newsletter-body"
                name="body"
                rows={9}
                maxLength={10000}
                required
                className="w-full resize-y rounded-lg border border-white/10 bg-slate-950 px-3 py-2.5 text-sm leading-6 text-white outline-none focus:border-cyan-300/50"
              />
            </div>
            {campaignStatus && (
              <p role="status" className={`text-sm ${campaignStatus.failedCount ? "text-amber-300" : "text-emerald-300"}`}>
                {campaignStatus.message}{campaignStatus.failedCount ? ` ${campaignStatus.failedCount} delivery failed.` : ""}
              </p>
            )}
            <button
              type="submit"
              disabled={sending || subscribedCount === 0}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-cyan-300 px-4 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending ? <Loader2 className="animate-spin" size={17} /> : <Send size={17} />}
              {sending ? "Sending newsletter..." : `Send to ${subscribedCount} subscribers`}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}